import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HistoryChatsService } from './historyChatsApi';
import { IHistoryChatListState } from '../../../../interfaces/history-chats/IHistoryChatsList';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { AxiosError } from 'axios';
import { ICards } from '../../../../interfaces/cards/ICards';

const initialState: IHistoryChatListState = {
    chatIsLoading: false,
    chatsAreLoading: false,
    cardIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToCreateCards = createAsyncThunk<any, ICards>(
    'historyChats/tryToCreateCards',
    async (data, { rejectWithValue }) => {
        try {
            const result = await HistoryChatsService.tryToCreateCards(data);
            return result;
        } catch (e) {
            const error = e as { response: { data: { message: string[] } } };
            if (error.response) {
                return rejectWithValue(
                    error.response.data?.message || 'Unknown error',
                );
            }
            return rejectWithValue('Network or unknown error');
        }
    },
);
export const tryToEditCard = createAsyncThunk<any, ICards>(
    'historyChats/tryToEditCard',
    async (data, { rejectWithValue }) => {
        try {
            const result = await HistoryChatsService.tryToEditCards(data);
            return result;
        } catch (e) {
            const error = e as { response: { data: { message: string[] } } };
            if (error.response) {
                return rejectWithValue(
                    error.response.data?.message || 'Unknown error',
                );
            }
            return rejectWithValue('Network or unknown error');
        }
    },
);
export const tryToGetCards = createAsyncThunk<any, void>(
    'historyChats/tryToFetchCards',
    async () => {
        const result = await HistoryChatsService.tryToFetchCards();
        return result.data;
    },
);
export const tryToDeleteCards = createAsyncThunk<any, string>(
    'historyChats/tryToDeleteCards',
    async (cardId) => {
        const result = await HistoryChatsService.tryToDeleteCards(cardId);
        return result;
    },
);

export const tryToFetchHistoryChats = createAsyncThunk<any, IPaginationPayload>(
    'historyChats/tryToFetchHistoryChats',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await HistoryChatsService.tryToFetchHistoryChats(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result;
    },
);

export const tryToFetchSingleChat = createAsyncThunk<any, string>(
    'historyChats/tryToFetchSingleChat',
    async (id: string) => {
        const result = await HistoryChatsService.tryToFetchSingleChat(id);
        return result?.data;
    },
);

export const tryToAddActionForChat = createAsyncThunk<any, any>(
    'historyChats/tryToAddActionForChat',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await HistoryChatsService.tryToAddActionForChat(
                id,
                data,
            );
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const historyChatsSlice = createSlice({
    name: 'historyChats',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToCreateCard
            .addCase(tryToCreateCards.pending, (state) => {
                state.cardIsLoading = true;
            })
            .addCase(tryToCreateCards.rejected, (state) => {
                state.cardIsLoading = false;
            })
            .addCase(tryToCreateCards.fulfilled, (state, action) => {
                state.cardIsLoading = false;
            })
			// tryToEditCard
            .addCase(tryToEditCard.pending, (state) => {
                state.cardIsLoading = true;
            })
            .addCase(tryToEditCard.rejected, (state) => {
                state.cardIsLoading = false;
            })
            .addCase(tryToEditCard.fulfilled, (state, action) => {
                state.cardIsLoading = false;
            })
            // tryToFetchCard
            .addCase(tryToGetCards.pending, (state) => {
                state.cardIsLoading = true;
            })
            .addCase(tryToGetCards.rejected, (state) => {
                state.cardIsLoading = false;
            })
            .addCase(tryToGetCards.fulfilled, (state, action) => {
                state.cardIsLoading = false;
            })
            // tryToFetchHistoryChats
            .addCase(tryToFetchHistoryChats.pending, (state) => {
                state.chatsAreLoading = true;
            })
            .addCase(tryToFetchHistoryChats.rejected, (state) => {
                state.chatsAreLoading = false;
            })
            .addCase(tryToFetchHistoryChats.fulfilled, (state, action) => {
                state.chatsAreLoading = false;
                state.historyChats = action.payload;
            })

            // tryToFetchSingleChat
            .addCase(tryToFetchSingleChat.pending, (state) => {
                state.chatIsLoading = true;
            })
            .addCase(tryToFetchSingleChat.rejected, (state) => {
                state.chatIsLoading = false;
            })
            .addCase(tryToFetchSingleChat.fulfilled, (state, action) => {
                state.chatIsLoading = false;
                state.historyChat = action.payload;
            });
    },
});

export default historyChatsSlice.reducer;
